import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  fetchPermitById,
  updatePermitRecord,
} from '../api/govopsPermitService';
import { formatGovOpsError } from '../api/govopsErrors';
import { formatStatus, formatTimestamp } from '../constants/workflowLabels';
import { validateCoordinates } from '../utils/mapCoordinates';
import { buildUpdatePermitPayload, getPermitDisplayFields } from '../utils/permitFields';
import { printPermitNotice } from '../utils/pdfExport';
import PageShell from './PageShell';
import PermitEditPanel from './PermitEditPanel';
import PermitFeePanel from './PermitFeePanel';

const STATUS_CLASS = {
  intake: 'rhs-badge--intake',
  under_review: 'rhs-badge--review',
  notice_sent: 'rhs-badge--notice',
  violation_open: 'rhs-badge--violation',
  payment_pending: 'rhs-badge--payment',
  payment_received: 'rhs-badge--resolved',
  payment_overdue: 'rhs-badge--violation',
  renewal_due: 'rhs-badge--notice',
  renewal_notice_sent: 'rhs-badge--notice',
  expired: 'rhs-badge--violation',
  resolved: 'rhs-badge--resolved',
  closed: 'rhs-badge--closed',
  appealed: 'rhs-badge--appeal',
};

const PermitDetails = () => {
  const { permitId } = useParams();
  const [permit, setPermit] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const row = await fetchPermitById(permitId);
      setPermit(row || null);
      if (!row) setError('Permit not found.');
    } catch (err) {
      setError(formatGovOpsError(err));
      setPermit(null);
    } finally {
      setLoading(false);
    }
  }, [permitId]);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = () => {
    setEditDraft({ ...permit });
    setEditing(true);
    setSuccess('');
  };

  const handleSave = async () => {
    if (!editDraft?.id) return;
    setSaving(true);
    setError('');
    setSuccess('');
    const coordCheck = validateCoordinates(
      editDraft.coordinates?.latitude,
      editDraft.coordinates?.longitude
    );
    if (!coordCheck.ok) {
      setError(coordCheck.message);
      setSaving(false);
      return;
    }
    try {
      const updated = await updatePermitRecord(
        editDraft.id,
        buildUpdatePermitPayload({
          ...editDraft,
          coordinates: { latitude: coordCheck.lat, longitude: coordCheck.lng },
        })
      );
      setPermit(updated);
      setEditDraft(null);
      setEditing(false);
      setSuccess('Permit updated.');
    } catch (err) {
      setError(formatGovOpsError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageShell title="Permit details">
        <div className="rhs-loading" role="status">
          <span className="rhs-spinner" aria-hidden="true" />
          Loading permit…
        </div>
      </PageShell>
    );
  }

  const statusKey = permit?.status || 'intake';
  const badgeClass = STATUS_CLASS[statusKey] || 'rhs-badge--default';

  return (
    <PageShell
      title="Permit details"
      subtitle={permit ? `Reference ${permit.id}` : undefined}
      className="rhs-permit-details"
    >
      <nav className="rhs-quick-nav" aria-label="Permit navigation">
        <Link to="/permit-history" className="rhs-quick-nav-item">
          ← Permit history
        </Link>
        <Link to="/dashboard" className="rhs-quick-nav-item">
          Dashboard
        </Link>
      </nav>

      {error && (
        <div className="rhs-error-panel" role="alert">
          <strong>Error</strong>
          <p className="rhs-muted">{error}</p>
        </div>
      )}
      {success && <p className="rhs-success-banner" role="status">{success}</p>}

      {permit && !editing && (
        <>
          <div className="rhs-card rhs-permit-details-summary">
            <div className="rhs-card-header">
              <h2 className="rhs-card-title">{permit.type || 'Permit'}</h2>
              <span className={`rhs-badge ${badgeClass}`}>{formatStatus(statusKey)}</span>
            </div>
            <dl className="rhs-dl">
              <dt>Description</dt>
              <dd>{permit.description || '—'}</dd>
              {getPermitDisplayFields(permit).map(([label, value]) => (
                <React.Fragment key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </React.Fragment>
              ))}
              <dt>Coordinates</dt>
              <dd>
                {permit.coordinates
                  ? `${permit.coordinates.latitude}, ${permit.coordinates.longitude}`
                  : '—'}
              </dd>
              <dt>Submitted</dt>
              <dd>{formatTimestamp(permit.createdAt || permit.date)}</dd>
            </dl>
            <div className="rhs-toolbar-actions">
              <button type="button" className="rhs-btn" onClick={startEdit}>
                Edit permit
              </button>
              <button
                type="button"
                className="rhs-btn rhs-btn--secondary"
                onClick={() => printPermitNotice(permit)}
              >
                Export notice (PDF)
              </button>
              {permit.caseId && (
                <Link
                  to={`/cases/${permit.caseId}`}
                  className="rhs-btn rhs-btn--outline"
                >
                  Case timeline
                </Link>
              )}
            </div>
          </div>
          <PermitFeePanel permit={permit} onUpdated={load} isAdmin />
        </>
      )}

      {editing && editDraft && (
        <PermitEditPanel
          permit={editDraft}
          onChange={setEditDraft}
          onSave={handleSave}
          onCancel={() => {
            setEditing(false);
            setEditDraft(null);
          }}
          saving={saving}
        />
      )}
    </PageShell>
  );
};

export default PermitDetails;
